import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';
import { Label } from '@ui/components/ui/label';
import { Textarea } from '@ui/components/ui/textarea';
import { capitalize } from '@app/utils';
import { DisputeSprintFormValues, SprintProductEntity, SprintProductClientEntity } from '@data';
import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Skeleton } from '@ui/components/ui/skeleton';
import { type ApprovalRecipientsFieldHandle, type FormOptionType } from './ApprovalRecipientsField';
import { RecipientsSection, useDefaultRecipients } from './RecipientsSection';

type ValuesType = {
  remark: string;
  recipients: FormOptionType[];
};

export interface DisputePopupProps extends React.HTMLAttributes<HTMLDivElement> {
  sprintProduct: Partial<SprintProductClientEntity> | Partial<SprintProductEntity>;
  onClose: () => void;
  disputeSprintProduct: (values: DisputeSprintFormValues) => void;
  sprintId: string;
  productId: string;
  isLoading?: boolean;
  isDisputing: boolean;
  show?: boolean;
}

/**
 * The other answer to a sprint report. Where approving is a decision with nothing to write, a dispute is
 * the writing: the remark is what the team has to act on, so it is required and it leads the dialog.
 *
 * Who it reaches is settled the same way an approval's is, by the same control, because a remark
 * concerns exactly the people an approval would have.
 */
export const DisputePopup = ({
  sprintProduct,
  onClose,
  disputeSprintProduct,
  productId,
  sprintId,
  isLoading,
  isDisputing,
  show = true,
}: DisputePopupProps): ReactElement => {
  const { t } = useTranslation();

  const recipientsFieldRef = useRef<ApprovalRecipientsFieldHandle>(null);

  const recipients = useDefaultRecipients(sprintProduct?.id);

  const initialValues: ValuesType = useMemo(
    () => ({ remark: '', recipients: recipients.initial }),
    [recipients.initial],
  );

  const { control, handleSubmit, reset, setError, getValues, watch } = useForm<ValuesType>({
    defaultValues: initialValues,
  });

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const noRecipients = watch('recipients').length === 0;
  const noRemark = !watch('remark')?.trim();

  const busy = isDisputing || isLoading || recipients.isPending;

  const onDispute = (values: ValuesType) => {
    disputeSprintProduct({
      productId,
      sprintId,
      sprintProductId: sprintProduct?.id,
      remark: values.remark.trim(),
      disputeRecipients: Array.from(new Set(values.recipients.map(x => x.value))),
    });
  };

  return (
    <Dialog open={show} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {!isLoading ? (
              t('doYouWantToDispute', { name: capitalize(sprintProduct?.product?.name) })
            ) : (
              <Skeleton as="span" className="inline-block h-7 w-2/3" />
            )}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(() => {
            // Flush a valid email still typed in the "To" box (handleSubmit captured the snapshot
            // before this ran), then re-read the form values so it isn't dropped.
            if (recipientsFieldRef.current && !recipientsFieldRef.current.commitDraft()) {
              return;
            }
            onDispute(getValues() as unknown as ValuesType);
          })}
        >
          <div className="mb-6 flex flex-col gap-4">
            <Controller
              name="remark"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="dispute-remark">{t('disputeRemarkLabel')}</Label>
                  <Textarea
                    {...field}
                    id="dispute-remark"
                    rows={3}
                    placeholder={t('disputeRemarkPlaceholder')}
                    disabled={busy}
                    aria-invalid={!!error}
                    className={error ? 'border-destructive' : undefined}
                  />
                  {error?.message ? <p className="text-sm text-destructive">{error.message}</p> : null}
                </div>
              )}
            />

            <div>
              <p className="mb-4 text-sm text-muted-foreground">{t('forwardDisputeAlwaysHint')}</p>

              <Controller
                name="recipients"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <RecipientsSection
                    ref={recipientsFieldRef}
                    recipients={recipients}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={busy}
                    error={error}
                    onDraftInvalid={() => setError('recipients', { type: 'manual', message: t('invalidEmailDraft') })}
                  />
                )}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              {t('actions.cancel')}
            </Button>
            {/* A dispute with nothing written is not a dispute, and one addressed to nobody goes nowhere. */}
            <Button type="submit" loading={isDisputing} disabled={busy || noRecipients || noRemark}>
              {t('actions.disputeAndForward')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
